import { useState } from "react";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import classes from "../../components/Modal.module.css";
import ErrorMessage from "../../components/ErrorMessage";
import saveIcon from "../../assets/save_icon.png";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import { onlyNums } from "../../utils/OnlyNums";
import { createDiscount } from "../../services/schoolDiscountsServices"; // Import the service
import Textarea from "../../components/Textarea";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import axiosClient from "../../services/axiosClient";

export default function AddDeductionsModal({ onClose, onSuccess }) {
  const { t } = useTranslation("general");
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [deduction, setDeduction] = useState({
    name: "",
    price: 0,
    notes: "",
  });

  const handleCancel = () => {
    onClose();
  };

  const handleAddDeduction = async () => {
    // Check if there is a name
    if (!deduction.name.trim()) {
      setValidationError("noDeductionName");
      return;
    }
    // Check if there is a price
    if (!deduction.price || parseFloat(deduction.price) <= 0) {
      setValidationError("invalidDeductionValue");
      return;
    }
    try {
      setLoadingModal(true);
      setError(null);

      // Create a deduction
      const response = await axiosClient.post(
        "/deductions/createDeduction",
        deduction
      );

      // Close the modal
      if (response.status === 200) {
        onSuccess();
      }
    } catch (err) {
      console.error("Error creating deduction:", err);
      setError("deductionCreationFailed");
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <>
      <Modal
        title="addDeduction"
        onClose={handleCancel}
        modalSize={"c-modal--small"}
        footer={
          <>
            {loadingModal ? <Loader size={LOADER_SIZES.MEDIUM} /> : null}
            {error ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(error)}
              </ErrorMessage>
            ) : null}
            {validationError ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(validationError)}
              </ErrorMessage>
            ) : null}
            <section className={classes.actions}>
              <CustomButton
                onClick={handleAddDeduction}
                title={t("confirm")}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleCancel}
                variant="blank"
              />
            </section>
          </>
        }
      >
        {error ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(error)}
          </ErrorBox>
        ) : null}
        {validationError ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(validationError)}
          </ErrorBox>
        ) : null}
        <div className="o-column o-column--gap-size_400">
          <CustomInput
            title={t("deductionName")}
            name="deductionName"
            value={deduction.name}
            onChange={(e) => {
              if (validationError === "noDeductionName") {
                setValidationError(null);
              }
              setDeduction((prev) => ({
                ...prev,
                name: e.target.value,
              }));
            }}
            flex={true}
            errorText={
              validationError === "noDeductionName" ? t("noDeductionName") : ""
            }
          />

          <CustomInputWithUnit
            name="price"
            title={t("price")}
            value={deduction.price}
            onChange={(e) => {
              if (validationError === "invalidDeductionValue") {
                setValidationError(null);
              }
              setDeduction((prev) => ({
                ...prev,
                price: e.target.value,
              }));
            }}
            onKeyDown={onlyNums}
            flex={true}
            min={0}
            max={100}
            unit="IQD"
            type="moneyV2"
            errorText={
              validationError === "invalidDeductionValue"
                ? t("invalidDeductionValue")
                : ""
            }
          />

          <div className="">
            <label>{t("notes")}</label>
            <Textarea
              name="notes"
              value={deduction.notes}
              onChange={(e) => {
                setDeduction((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }));
              }}
              flex={true}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
